/**
 * Floating "Self-assessment" CTA pill that appears on a ServicePage when the
 * service has a matching public lite assessment.
 *
 * Bottom-right, sticky, brand-purple, dismissable per-session.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, BarChart3 } from "lucide-react";

const SERVICE_TO_LITE = {
  "it-service-management-and-operational-excellence": { slug: "itsm360", brand: "ITSM360", questions: 7 },
  "ai-readiness-and-adoption": { slug: "ai360", brand: "AI360", questions: 6 },
  "data-insight-and-performance": { slug: "data360", brand: "DATA360", questions: 7 },
  "project-programme-and-pmo-improvement": { slug: "pmo360", brand: "PMO360", questions: 5 },
  "business-change-and-adoption": { slug: "change360", brand: "CHANGE360", questions: 5 },
  "digital-and-technology-transformation": { slug: "digital360", brand: "DIGITAL360", questions: 5 },
  "service-and-operating-model-transformation": { slug: "operating360", brand: "OPERATING360", questions: 5 },
  "supplier-and-technology-value-optimisation": { slug: "supplier360", brand: "SUPPLIER360", questions: 5 },
  "benefits-realisation-and-assurance": { slug: "benefits360", brand: "BENEFITS360", questions: 5 },
  "governance-and-delivery-control": { slug: "govern360", brand: "GOVERN360", questions: 5 },
  "capability-and-training-needs-analysis": { slug: "capability360", brand: "CAPABILITY360", questions: 5 },
  "stakeholder-engagement-and-communications": { slug: "stakeholder360", brand: "STAKEHOLDER360", questions: 5 },
};

const STORAGE_PREFIX = "ek-saCta-dismissed-";

export default function FloatingAssessmentCTA({ serviceSlug }) {
  const lite = SERVICE_TO_LITE[serviceSlug];
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!lite) return;
    const dismissed = sessionStorage.getItem(STORAGE_PREFIX + lite.slug);
    if (dismissed) return;
    const t = setTimeout(() => setVisible(true), 1800);
    return () => clearTimeout(t);
  }, [lite]);

  if (!lite || !visible) return null;

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem(STORAGE_PREFIX + lite.slug, "1");
    setVisible(false);
  };

  return (
    <Link
      to={`/resources/${lite.slug}-self-assessment`}
      style={{ bottom: "calc(1.5rem + var(--ek-cookie-bar-h, 0px))" }}
      className="fixed right-6 z-40 bg-[#0B0B0C] text-white border border-[#7737F6]/40 shadow-2xl shadow-[#7737F6]/20 px-5 py-3 hidden md:flex items-center gap-3 max-w-[360px] hover:bg-[#16161A] transition-colors group"
      data-testid="floating-assessment-cta"
      aria-label={`Take the ${lite.brand} self-assessment`}
    >
      <div className="inline-flex items-center justify-center w-9 h-9 bg-[#7737F6] text-white flex-shrink-0">
        <BarChart3 size={18} />
      </div>
      <div className="flex-1 leading-tight">
        <div className="text-[10px] font-bold tracking-[0.18em] text-[#A179FA] uppercase">{lite.brand} self-assessment</div>
        <div className="text-[13px] mt-0.5">{lite.questions} questions · 3 min · instant radar</div>
      </div>
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="text-zinc-500 hover:text-white p-1 -mr-2" data-testid="floating-assessment-cta-dismiss">
        <X size={14} />
      </button>
    </Link>
  );
}
